import { Box, Table, Text } from "@radix-ui/themes";
import { DashboardWidget, ListData } from "@/types/breeze/Dashboard";
import { formatDate } from "@/utils/date";

interface Props {
  widget: DashboardWidget;
  data?: ListData;
  loading?: boolean;
}

// Matches an ISO 8601 date string — used to decide when to humanise a value
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export function ListWidget({ widget, data, loading }: Props) {
  const config = parseConfig(widget.config_json);
  const fieldNames = (config?.fields ?? "*")
    .split(",")
    .map((f) => f.trim())
    .filter((f) => f && f !== "*");

  return (
    <Box p="4" style={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <Text size="1" color="gray" mb="3" asChild>
        <div>{widget.title}</div>
      </Text>
      {loading && <Text size="2" color="gray">Loading…</Text>}
      {!loading && (!data?.items || data.items.length === 0) && (
        <Text size="2" color="gray">No records</Text>
      )}
      {!loading && data?.items && data.items.length > 0 && (
        <Box style={{ overflow: "auto", flex: 1 }}>
          <Table.Root size="1" variant="ghost">
            {fieldNames.length > 0 && (
              <Table.Header>
                <Table.Row>
                  {fieldNames.map((field) => (
                    <Table.ColumnHeaderCell key={field}>{field}</Table.ColumnHeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
            )}
            <Table.Body>
              {data.items.map((item, i) => (
                <Table.Row key={i}>
                  {fieldNames.length > 0 ? (
                    fieldNames.map((field) => (
                      <Table.Cell key={field}>{formatCell(item[field])}</Table.Cell>
                    ))
                  ) : (
                    <Table.Cell>{JSON.stringify(item).slice(0, 120)}</Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Box>
  );
}

function formatCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "string" && ISO_DATE_RE.test(value)) return formatDate(value);
  if (typeof value === "object") return JSON.stringify(value).slice(0, 80);
  return String(value);
}

function parseConfig(configJson: string | null): { entityName?: string; fields?: string; limit?: number } | null {
  if (!configJson) return null;
  try {
    return JSON.parse(configJson);
  } catch {
    return null;
  }
}
